/**
 * MobileAuditFindingsList — flat findings feed ( <lg ). Batch 6.3.
 * GET /audits-qa/findings/all — same status/priority filters as desktop.
 * Finding text stays paired with its corrective action (single card).
 * All Add/Edit/Delete flows through AuditFullDrawer (opened via
 * onOpenAudit(f.audit_id)) — no mobile mutation path.
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Clock, CheckCircle2, AlertOctagon, AlertTriangle, Circle } from "lucide-react";

const STATUS_META = {
  open:        { label: "Open",        Icon: Circle,       cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_progress: { label: "In Progress", Icon: Clock,        cls: "bg-sky-100 text-sky-800 border-sky-300" },
  completed:   { label: "Completed",   Icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  overdue:     { label: "Overdue",     Icon: AlertOctagon, cls: "bg-red-100 text-red-800 border-red-300" },
};

const PRIORITY_META = {
  low:      { label: "Low",      Icon: Circle,        cls: "bg-slate-100 text-slate-700 border-slate-200" },
  medium:   { label: "Medium",   Icon: Circle,        cls: "bg-sky-100 text-sky-800 border-sky-200" },
  high:     { label: "High",     Icon: AlertTriangle, cls: "bg-amber-100 text-amber-900 border-amber-300" },
  critical: { label: "Critical", Icon: AlertOctagon,  cls: "bg-red-100 text-red-800 border-red-200" },
};

function Pill({ meta, testid, extraData }) {
  if (!meta) return null;
  const Icon = meta.Icon;
  return (
    <span data-testid={testid} {...(extraData || {})}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function MobileAuditFindingsList({ onOpenAudit, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...(status ? { status } : {}), ...(priority ? { priority } : {}) };
      const r = await api.get("/audits-qa/findings/all", { params });
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status, priority]);

  useEffect(() => { load(); }, [load, refreshTick]);

  return (
    <div className="space-y-3" data-testid="mobile-audits-findings">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex-1 min-w-[130px]">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="mobile-findings-filter-status"
            className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize">
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </select>
        </label>
        <label className="flex-1 min-w-[130px]">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Priority</span>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="mobile-findings-filter-priority"
            className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize">
            <option value="">All priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </label>
      </div>

      <div className="text-xs text-slate-500 tabular-nums" data-testid="mobile-findings-count">
        {rows.length} finding{rows.length === 1 ? "" : "s"}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 p-4" data-testid="mobile-findings-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-findings-empty">
          <div className="text-sm font-semibold text-slate-700">No findings match — nice work.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mobile-findings-list">
          {rows.map((f) => (
            <li key={f.id} data-testid={`mobile-finding-row-${f.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
              {/* Row 1 — Finding text */}
              <div className="text-sm font-semibold text-slate-900" data-testid={`mobile-finding-text-${f.id}`}>
                {f.finding || "—"}
              </div>
              {/* Row 2 — Corrective action (paired with finding, 2-line clamp) */}
              <div className="text-[12px] text-slate-600 mt-1 line-clamp-2" data-testid={`mobile-finding-corrective-${f.id}`}>
                <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Action</span>
                {f.corrective_action || "—"}
              </div>
              {/* Row 3 — Assigned · Due */}
              <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                <span data-testid={`mobile-finding-assigned-${f.id}`}>{f.assigned_to_name || "Unassigned"}</span>
                <span className="text-slate-400">·</span>
                <span className="tabular-nums" data-testid={`mobile-finding-due-${f.id}`}>Due {f.due_date || "—"}</span>
              </div>
              {/* Row 4 — Priority + Status pills (server-authoritative) */}
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                <Pill meta={PRIORITY_META[(f.priority || "medium").toLowerCase()]}
                  testid={`mobile-finding-priority-${f.id}`}
                  extraData={{ "data-priority": f.priority }} />
                <Pill meta={STATUS_META[f.status || "open"]}
                  testid={`mobile-finding-status-${f.id}`}
                  extraData={{ "data-status": f.status }} />
                <button
                  type="button"
                  onClick={() => onOpenAudit?.(f.audit_id)}
                  data-testid={`btn-mobile-open-finding-audit-${f.id}`}
                  className="ml-auto min-h-[44px] shrink-0 inline-flex items-center gap-1 text-xs font-medium text-sky-700"
                >
                  Open audit <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
